import { useState, useEffect } from "react";
import axios from "axios";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  MenuItem,
  Typography,
  IconButton,
  Checkbox,
  FormControlLabel,
  Autocomplete,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import CloseIcon from "@mui/icons-material/Close";
import dayjs from "dayjs";
import { debounce } from "lodash";
import useAxiosInstance from "../helpers/useAxiosInstance";

const commissionTypes = [
  { value: "percentage", label: "%" },
  { value: "flat", label: "Flat" },
];

const initialFormState = {
  invoiceNumber: "",
  fullName: "",
  roommate: "",
  propertyName: "",
  applicationDate: null,
  moveInDate: null,
  unit: "",
  referral: "",
  leaseTerm: "",
  leaseEndDate: null,
  rent: "",
  commissionType: "percentage",
  commission: "",
  paid: false,
};

const referralOptions = [
  { label: "AR", value: "AR" },
  { label: "CL", value: "CL" },
];

export default function AddCommissionModal({
  open,
  setOpen,
  handleAddSubmit = () => {},
  messageApi,
  currentRow,
  setCurrentRow = () => {},
  entity
}) {
  
  const [formData, setFormData] = useState(initialFormState);
  const [loading, setLoading] = useState(false);
  const [clientList, setClientList] = useState([]);
  const [propertiesList, setPropertiesList] = useState([]);

  const axiosInstance = useAxiosInstance()

  useEffect(() => {
    if (currentRow) {
      setFormData({
        invoiceNumber: currentRow?.invoice_number?.toString() || "",
        fullName: currentRow?.full_name || "",
        roommate: currentRow?.roommate || "",
        propertyName: currentRow?.property_name || "",
        applicationDate: currentRow?.application_date
          ? dayjs(currentRow?.application_date)
          : null,
        moveInDate: currentRow?.move_in_date
          ? dayjs(currentRow?.move_in_date)
          : null,
        unit: currentRow?.unit || "",
        referral: currentRow?.referral || "",
        leaseTerm: currentRow?.lease_term || "",
        leaseEndDate: currentRow?.lease_end_date
          ? dayjs(currentRow?.lease_end_date)
          : null,
        rent: currentRow?.rent || "",
        commissionType: currentRow?.commission_type || "percentage",
        commission: currentRow?.commission || "",
        paid: currentRow?.paid || false,
      });
    } else {
      setFormData(initialFormState);
    }
  }, [currentRow, open]);

  useEffect(() => {
  if (formData?.moveInDate && formData?.leaseTerm) {
    const leaseEnd = dayjs(formData.moveInDate).add(
      Number(formData.leaseTerm),
      "month"
    );
    setFormData((prev) => ({
      ...prev,
      leaseEndDate: leaseEnd,
    }));
  }
}, [formData.moveInDate, formData.leaseTerm]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleDateChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleClose = () => {
    if (loading) return;
    setCurrentRow(null);
    setFormData(initialFormState);
    setOpen(false);
  };

  const handleSave = async () => {
    if (!isFormValid) {
      messageApi.warning("Please fill all required fields correctly.");
      return;
    }

    try {
      setLoading(true);

      const payload = {
        ...formData,
        applicationDate: formData.applicationDate?.toISOString(),
        moveInDate: formData.moveInDate?.toISOString(),
        leaseEndDate: formData.leaseEndDate?.toISOString(),
        entity
      };

      const url =
        currentRow && currentRow?.id
          ? `${process.env.REACT_APP_API_URL}/commission/update-commission/${currentRow?.id}`
          : `${process.env.REACT_APP_API_URL}/commission/add-commission`;

      const method = currentRow ? axiosInstance.put : axiosInstance.post;

      const response = await method(url, payload);

      if (response?.data?.success) {
        messageApi.success(
          currentRow
            ? "Commission updated successfully."
            : "Commission added successfully."
        );
        setFormData(initialFormState);
        handleAddSubmit();
        setOpen(false);
      } else {
        messageApi.error(
          response?.data?.message || "Operation failed. Please try again."
        );
      }
    } catch (error) {
      console.error("Error during save/update:", error);
      messageApi.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const isValidNumber = (value) => /^\d+(\.\d{1,2})?$/.test(value);

  const isFormValid =
    formData?.invoiceNumber?.trim() !== "" &&
    formData?.fullName?.trim() !== "" &&
    !/\d/.test(formData?.fullName) &&
    formData?.propertyName?.trim() !== "" &&
    formData?.moveInDate !== null &&
    formData?.unit?.trim() !== "" &&
    // /^[0-9A-Za-z]+$/.test(formData?.unit) &&
    formData?.referral?.trim() !== "" &&
    formData?.rent?.trim() !== "" &&
    isValidNumber(formData?.rent) &&
    formData?.commissionType?.trim() !== "" &&
    formData?.commission?.trim() !== "" &&
    isValidNumber(formData?.commission) &&
    (formData?.leaseTerm?.trim() === "" || isValidNumber(formData?.leaseTerm));

  const fetchClientList = debounce(async (searchText) => {
    if (searchText.length > 3) {
      setLoading(true);
      try {
        const response = await axiosInstance.get(
          `${process.env.REACT_APP_API_URL}/clients/${searchText}`
        );

        setClientList(response?.data?.map((client) => {
          return {
            label: client?.fullName ?? "",
            value: client?.id ?? ""
          }
        })); // Extract names
      } catch (error) {
        console.error("Error fetching clientList:", error);
      } finally {
        setLoading(false);
      }
    } else {
      setClientList([]);
    }
  }, 300);
  const fetchPropertiesList = debounce(async (searchText) => {
    if (searchText.length > 3) {
      setLoading(true);
      try {
        const encodedSearchText = encodeURIComponent(searchText);
        const response = await axiosInstance.get(
          `${process.env.REACT_APP_API_URL}/properties/${encodedSearchText}`
        );

        setPropertiesList(response?.data?.map((property) => property?.name)); // Extract names
      } catch (error) {
        console.error("Error fetching properties:", error);
      } finally {
        setLoading(false);
      }
    } else {
      setPropertiesList([]);
    }
  }, 300);

  const handleInputClientName = (event, value) => {
    setFormData((prev) => ({ ...prev, fullName: value }));
    fetchClientList(value);
  };
  const handleInputPropertyName = (event, value) => {
    setFormData((prev) => ({ ...prev, propertyName: value }));
    fetchPropertiesList(value);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Dialog
        open={open}
        handleClose={handleClose}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <div className="flex items-center justify-between">
            <Typography variant="h6">
              {currentRow && currentRow?.id
                ? "Edit Commission"
                : "Add Commission"}
            </Typography>
            <IconButton onClick={handleClose} disabled={loading}>
              <CloseIcon />
            </IconButton>
          </div>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              {/* <TextField
                size="small"
                fullWidth
                required
                label="Client Name"
                name="fullName"
                value={formData?.fullName || ""}
                onChange={handleChange}
                onBeforeInput={(e) => {
                  if (/\d/.test(e.data)) {
                    e.preventDefault();
                  }
                }}
                inputProps={{
                  inputMode: "text",
                  pattern: "[A-Za-z ]*",
                }}
              /> */}
              <Autocomplete
                size="small"
                fullWidth
                inputProps={{
                  inputMode: "text",
                  pattern: "[A-Za-z ]*",
                }}
                loading={loading}
                value={formData?.fullName}
                onChange={(event, newValue) =>{
                    const clientObj = clientList.find(option=>option?.label === newValue)
                    setFormData((prev) => ({ 
                      ...prev, 
                      fullName: newValue,
                      client_id: clientObj?.value ?? null,
                    }))
                  }
                }
                onInputChange={handleInputClientName}
                options={clientList.map(option=>option?.label)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Client Name"
                    placeholder="Client Name"
                    required
                    variant="outlined"
                    name="fullName"
                  />
                )}
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                size="small"
                fullWidth
                label="Invoice Number"
                name="invoiceNumber"
                value={formData?.invoiceNumber || ""}
                onChange={handleChange}
                type="text"
                inputProps={{ step: "1", min: 0 }}
              />
            </Grid>

            <Grid item xs={6}>
              {/* <TextField
                size="small"
                fullWidth
                required
                label="Property"
                name="propertyName"
                value={formData?.propertyName || ""}
                onChange={handleChange}
              /> */}
              <Autocomplete
                size="small"
                label="Property"
                fullWidth
                inputProps={{
                  inputMode: "text",
                  pattern: "[A-Za-z ]*",
                }}
                loading={loading}
                value={formData?.propertyName}
                onChange={(event, newValue) =>
                  setFormData((prev) => ({ ...prev, propertyName: newValue }))
                }
                onInputChange={handleInputPropertyName}
                options={propertiesList}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Property"
                    required
                    placeholder="Property"
                    variant="outlined"
                    name="propertyName"
                  />
                )}
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                size="small"
                fullWidth
                required
                label="Unit Number"
                name="unit"
                value={formData?.unit || ""}
                onChange={handleChange}
                type="text"
                inputProps={{ step: "1", min: 0 }}
              />
            </Grid>

            <Grid item xs={6}>
              <DatePicker
                label="Application Date"
                value={formData?.applicationDate || null}
                onChange={(value) => handleDateChange("applicationDate", value)}
                slotProps={{
                  textField: { size: "small", fullWidth: true },
                }}
              />
            </Grid>

            <Grid item xs={6}>
              <DatePicker
                label="Move In Date"
                value={formData?.moveInDate || null}
                onChange={(value) => handleDateChange("moveInDate", value)}
                slotProps={{
                  textField: { size: "small", fullWidth: true, required: true },
                }}
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                size="small"
                fullWidth
                // required
                label="Lease Length"
                name="leaseTerm"
                value={formData?.leaseTerm || ""}
                onChange={handleChange}
                type="number"
                inputProps={{ step: "0.01", min: 0 }}
              />
            </Grid>

            <Grid item xs={6}>
              <DatePicker
                label="Lease End Date"
                value={formData?.leaseEndDate || null}
                onChange={(value) => handleDateChange("leaseEndDate", value)}
                slotProps={{
                  textField: { size: "small", fullWidth: true },
                }}
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                size="small"
                fullWidth
                required
                select
                label="Referral Source"
                name="referral"
                value={formData?.referral || ""}
                onChange={handleChange}
              >
                {referralOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={6}>
              <TextField
                size="small"
                fullWidth
                required
                label="Rent Amount"
                name="rent"
                value={formData?.rent || ""}
                onChange={handleChange}
                type="number"
                inputProps={{ step: "0.01", min: 0 }}
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                select
                size="small"
                fullWidth
                required
                label="Commission Type"
                name="commissionType"
                value={formData?.commissionType || ""}
                onChange={handleChange}
              >
                {commissionTypes.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={6}>
              <TextField
                size="small"
                fullWidth
                required
                label="Commission"
                name="commission"
                value={formData?.commission || ""}
                onChange={handleChange}
                type="number"
                inputProps={{ step: "0.01", min: 0 }}
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                size="small"
                fullWidth
                label="Roommate"
                name="roommate"
                value={formData?.roommate || ""}
                onChange={handleChange}
                type="text"
              />
            </Grid>

            <Grid item xs={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData?.paid}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        paid: e?.target?.checked,
                      }))
                    }
                  />
                }
                label="Paid"
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ pr: 3, pb: 2 }}>
          <Button
            onClick={handleClose}
            disabled={loading}
            variant="outlined"
            size="small"
            color="error"
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            size="small"
            color="success"
            onClick={handleSave}
            disabled={!isFormValid || loading}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
}
